import { useState } from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { humanizeKey } from "../utils";

/** Collapsible key/value view of the structured `defaults` (assumptions). */
export function DefaultsPanel({
  defaults,
}: {
  defaults: Record<string, unknown>;
}) {
  const [open, setOpen] = useState(false);

  // `anchor_date` defaults to today with source "today (fallback)" whenever the
  // question names no as-of date (the common case). Showing, say,
  // "2026-07-01 / today (fallback)" next to a question about 2023 reads as a
  // mismatch even though it's correct (the year is the query window, not the
  // as-of anchor). Hide both anchor rows in that fallback case; keep them when
  // the anchor was actually meaningful (an explicit flag, or one extracted from
  // the question).
  const anchorIsFallback = defaults?.anchor_date_source === "today (fallback)";
  const entries = Object.entries(defaults ?? {}).filter(
    ([k]) =>
      !(anchorIsFallback && (k === "anchor_date" || k === "anchor_date_source")),
  );
  if (entries.length === 0) return null;

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Info className="h-4 w-4 text-slate-500" />
          Defaults &amp; assumptions
          <span className="text-xs text-slate-500">({entries.length})</span>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" />
        )}
      </button>
      {open && (
        <div className="border-t border-slate-200 divide-y divide-slate-50">
          {entries.map(([k, v]) => (
            <div
              key={k}
              className="px-4 py-2 flex items-baseline gap-2 text-xs"
            >
              <span className="text-slate-400 min-w-[8rem]">{humanizeKey(k)}</span>
              <span className="text-slate-800 font-mono break-all">
                {typeof v === "object" ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
